/*
  Size Chart (PDP block)
  ----------------------
  Opens the product's size chart image in the shared MinimogTheme.Modal, which
  already provides the overlay, the close button, ESC, the backdrop click, the
  `prevent-scroll` lock and the scale+fade animation. This file only adds what
  that modal has no opinion about: where the content comes from, and the
  dialog semantics / focus handling it does not implement.

  The chart markup lives in an inert <template> in the Liquid. Template content
  is a detached DocumentFragment, so the parser never creates the <img> and the
  image cannot be requested until the fragment is cloned on first open - a
  guarantee a hidden <div> cannot give (a display:none lazy image is only
  usually deferred).
*/
if (!customElements.get("m-size-chart")) {
  customElements.define(
    "m-size-chart",
    class MSizeChart extends HTMLElement {
      constructor() {
        super();
        this.selectors = {
          openButton: "[data-open-size-chart]",
          template: "[data-size-chart-template]",
          modalContent: ".m-modal--content",
          modalClose: ".m-modal--close",
        };
      }

      connectedCallback() {
        this.template = this.querySelector(this.selectors.template);
        if (!this.template) return;
        this.init();
      }

      disconnectedCallback() {
        if (this.unbindOpen) this.unbindOpen();
        if (this.unsubscribeClosed) this.unsubscribeClosed();
      }

      init() {
        this.modal = new MinimogTheme.Modal();

        // `context: this` keeps the delegate on this element - no listener is
        // added to document for the trigger.
        this.unbindOpen = addEventDelegate({
          context: this,
          selector: this.selectors.openButton,
          handler: (e, target) => {
            e.preventDefault();
            this.open(target);
          },
        });

        // The one global subscription, and it is load bearing: the shared modal
        // restores no focus of its own, so without this the user is dumped at
        // the top of the document on close. Guarded on the emitting instance so
        // another component's modal closing cannot steal focus.
        this.unsubscribeClosed = window.MinimogEvents.subscribe("ON_MODAL_CLOSED", (modal) => {
          if (modal !== this.modal || !this.trigger) return;
          removeTrapFocus(this.trigger);
          this.trigger = null;
        });
      }

      open(trigger) {
        const dialog = document.createElement("DIV");
        dialog.setAttribute("role", "dialog");
        dialog.setAttribute("aria-modal", "true");
        dialog.setAttribute("aria-label", this.dataset.dialogLabel);
        // Cloned per open: the shared modal destroys its children on close. The
        // <template> is parsed once, so the image is fetched on the first open
        // and served from cache after that.
        dialog.appendChild(this.template.content.cloneNode(true));

        this.modal.appendChild(dialog);
        this.modal.setWidth("720px");
        this.modal.setSizes(`m-size-chart-modal m-gradient ${this.dataset.colorScheme}`);
        this.trigger = trigger;
        this.modal.open();

        // open() appends synchronously, so the content is in the DOM here.
        // Trapping on .m-modal--content covers both the chart and the modal's
        // own close button; focusing that button is what engages the theme's
        // trapFocus keydown handler straight away.
        const modalContent = this.modal.modalContent;
        if (modalContent) {
          trapFocus(modalContent, modalContent.querySelector(this.selectors.modalClose) || modalContent);
        }
      }
    }
  );
}
